import EChartsReact from 'echarts-for-react';
import { useMemo } from 'react';
import { Typography } from '@analytical-alley/ui';

import { GREEN } from './constants';

const ACCURACY_BAR_MAX = 100;

type ModelAccuracyChartProps = {
    weeklyAccuracy: number | null | undefined;
    monthlyAccuracy: number | null | undefined;
};

export const ModelAccuracyChart = ({
    weeklyAccuracy,
    monthlyAccuracy,
}: ModelAccuracyChartProps) => {
    const items = useMemo(
        () =>
            [
                { label: 'Monthly', accuracy: monthlyAccuracy },
                { label: 'Weekly', accuracy: weeklyAccuracy },
            ].filter(
                (entry): entry is { label: string; accuracy: number } =>
                    entry.accuracy != null,
            ),
        [weeklyAccuracy, monthlyAccuracy],
    );

    const option = useMemo(
        () => ({
            grid: {
                top: 4,
                bottom: 18,
                left: 70,
                right: 16,
                containLabel: false,
            },
            xAxis: {
                type: 'value',
                min: 0,
                max: ACCURACY_BAR_MAX,
                axisLabel: {
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: 10,
                    formatter: '{value}%',
                },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                    lineStyle: { color: 'rgba(255,255,255,0.08)' },
                },
            },
            yAxis: {
                type: 'category',
                data: items.map((d) => d.label),
                axisLabel: {
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: 11,
                },
                axisLine: { show: false },
                axisTick: { show: false },
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                valueFormatter: (v: number) => `${v.toFixed(2)}%`,
            },
            series: [
                {
                    type: 'bar',
                    data: items.map((d) =>
                        Math.min(d.accuracy, ACCURACY_BAR_MAX),
                    ),
                    barWidth: 12,
                    itemStyle: {
                        borderRadius: [0, 4, 4, 0],
                        color: GREEN,
                    },
                    label: {
                        show: true,
                        position: 'insideRight',
                        color: 'rgba(0,0,0,0.8)',
                        fontSize: 11,
                        fontWeight: 'bold',
                        formatter: (params: { dataIndex: number }) => {
                            const value = items[params.dataIndex]?.accuracy;
                            return value != null ? `${value.toFixed(2)}%` : '';
                        },
                    },
                },
            ],
        }),
        [items],
    );

    if (items.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1">
            <Typography
                variant="bodyS"
                tooltip="How close the model KPI predictions are on average to the actual business results."
            >
                Model Accuracy
            </Typography>
            <EChartsReact
                option={option}
                notMerge
                style={{ height: items.length * 32 + 24, width: '100%' }}
                opts={{ renderer: 'svg' }}
            />
        </div>
    );
};
